// Queries + mutations for the Phase 5/6 resolution workflow.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, postJson } from './client'
import { stepUpPost } from '../lib/stepup'
import type { PageOf, ReasonCodeSpec, Resolution, ResolutionProgress, Upload } from './types'

export function useReasonCodes() {
  return useQuery({
    queryKey: ['reason-codes'],
    queryFn: () => api<Record<string, ReasonCodeSpec>>('/reason-codes'),
    staleTime: Infinity,
  })
}

export function useRunResolutions(runId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['resolutions', runId],
    queryFn: () => api<Resolution[]>(`/runs/${runId}/resolutions`),
    enabled: enabled && Boolean(runId),
  })
}

export function useResolutionProgress(runId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['resolution-progress', runId],
    queryFn: () => api<ResolutionProgress>(`/runs/${runId}/resolution-progress`),
    // Reconciliation has no Refresh button any more (Stage 61) — its button
    // starts a real run. These re-read on focus so a reviewer coming back to
    // the tab sees the preparer's latest work without paying for one.
    refetchOnWindowFocus: true,
    enabled: enabled && Boolean(runId),
  })
}

export function useReviewQueue(clientId: string, page = 1, size = 100) {
  return useQuery({
    queryKey: ['review-queue', clientId, page, size],
    queryFn: () =>
      api<PageOf<Resolution> & { reversals: Resolution[] }>(
        `/clients/${clientId}/review-queue?page=${page}&size=${size}`),
    enabled: Boolean(clientId),
    placeholderData: keepPreviousData,
  })
}

export function useOrphanedResolutions(clientId: string) {
  return useQuery({
    queryKey: ['orphaned-resolutions', clientId],
    queryFn: () => api<Resolution[]>(`/clients/${clientId}/resolutions?orphaned=true`),
    enabled: Boolean(clientId),
  })
}

export interface ResolvePayload {
  fingerprint: string
  item_kind: 'party' | 'residual'
  reason_code: string
  comment?: string
  details?: Record<string, string>
  evidence_upload_id?: string
}

export interface BulkResolvePayload {
  fingerprints: string[]
  item_kind: 'party' | 'residual'
  reason_code: string
  comment?: string
  details?: Record<string, string>
}

/** Invalidate everything a resolution state change can affect. */
function useInvalidateResolutions() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['resolutions'] })
    void qc.invalidateQueries({ queryKey: ['resolution-progress'] })
    void qc.invalidateQueries({ queryKey: ['review-queue'] })
    void qc.invalidateQueries({ queryKey: ['orphaned-resolutions'] })
  }
}

export function useResolveMutation(runId: string) {
  const invalidate = useInvalidateResolutions()
  return useMutation({
    mutationFn: (p: ResolvePayload) => postJson<Resolution>(`/runs/${runId}/resolutions`, p),
    onSuccess: invalidate,
  })
}

export function useBulkResolveMutation(runId: string) {
  const invalidate = useInvalidateResolutions()
  return useMutation({
    mutationFn: (p: BulkResolvePayload) =>
      postJson<Resolution[]>(`/runs/${runId}/resolutions/bulk`, p),
    onSuccess: invalidate,
  })
}

export function useApproveMutation() {
  const invalidate = useInvalidateResolutions()
  return useMutation({
    // the review verbs are step-up gated (guide §7.2) — the assertion comes
    // from lib/stepup, the one home for that seam
    mutationFn: (resolutionId: string) =>
      stepUpPost<Resolution>(`/resolutions/${resolutionId}/approve`, {}),
    onSuccess: invalidate,
  })
}

export function useRejectMutation() {
  const invalidate = useInvalidateResolutions()
  return useMutation({
    mutationFn: (p: { resolutionId: string; comment: string }) =>
      stepUpPost<Resolution>(`/resolutions/${p.resolutionId}/reject`, { comment: p.comment }),
    onSuccess: invalidate,
  })
}

export function useRegenerateMutation(runId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => postJson<{ status: string }>(`/runs/${runId}/documents/regenerate`, {}),
    onSuccess: () => {
      // documents regenerate in the background - refresh the list shortly after
      window.setTimeout(() => void qc.invalidateQueries({ queryKey: ['documents', runId] }), 4000)
      window.setTimeout(() => void qc.invalidateQueries({ queryKey: ['documents', runId] }), 12000)
    },
  })
}

/** Multipart evidence upload into the run's period (kind=evidence). */
export async function uploadEvidence(periodId: string, file: File): Promise<Upload> {
  const fd = new FormData()
  fd.append('file', file)
  return api<Upload>(`/periods/${periodId}/uploads?kind=evidence`, {
    method: 'POST',
    body: fd,
  })
}
